import { useState, type ReactNode } from 'react';

import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui/button';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Input } from '../../../shared/ui/input';
import { Note } from '../../../shared/ui/note';
import { SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState } from '../../../shared/ui/states';
import {
  VerifySteps,
  parseHeaderLines,
  verdictOf,
  verifySteps,
  type TAuthPresetSpec,
  type TVerifyResult,
} from '../../../entities/auth-preset';
import { SampleEditor } from './sample-editor';
import { sampleIsUsable, type ISampleDraft } from '../model';

const VERDICT_TONE: Record<string, string> = {
  matches: 'text-success',
  'does not match': 'text-danger',
  'reading only': 'text-muted',
};

// Three verdicts, not two: with no secret `signatureMatched` is null, which is not false.
const Verdict = ({
  result,
  stale,
  blocked,
}: {
  result: TVerifyResult | undefined;
  stale: boolean;
  blocked?: string | null;
}) => {
  if (!result || blocked) {
    return <span className="font-mono text-micro text-faint">not checked yet</span>;
  }
  const verdict = verdictOf(result);
  return (
    <span className={cn('font-mono text-micro', stale ? 'text-faint' : VERDICT_TONE[verdict])}>
      {stale ? 'checking…' : verdict}
    </span>
  );
};

const SampleSummary = ({ sample }: { sample: ISampleDraft }) => {
  const headers = Object.entries(parseHeaderLines(sample.headersText));
  return (
    <>
      {headers.slice(0, 2).map(([name, value]) => (
        <p key={name} className="truncate font-mono text-meta leading-17 text-faint">
          {name}: {value}
        </p>
      ))}
      {sample.body && (
        <p className="mt-label truncate font-mono text-meta leading-17 text-muted">{sample.body}</p>
      )}
    </>
  );
};

interface IVerifyPanelProps {
  title: string;
  spec: TAuthPresetSpec | null;
  sample: ISampleDraft;
  onSampleChange: (next: ISampleDraft) => void;
  secret: string;
  onSecretChange: (next: string) => void;
  secretLabel?: string;
  secretElsewhere?: boolean;
  result: TVerifyResult | undefined;
  error: unknown;
  stale: boolean;
  blocked?: string | null;
  footer?: ReactNode;
  note?: ReactNode;
  className?: string;
}

export const VerifyPanel = ({
  title,
  spec,
  sample,
  onSampleChange,
  secret,
  onSecretChange,
  secretLabel = 'Secret from the sender, to take the check all the way',
  secretElsewhere,
  result,
  error,
  stale,
  blocked,
  footer,
  note,
  className,
}: IVerifyPanelProps) => {
  const usable = sampleIsUsable(sample);
  const [editing, setEditing] = useState(!usable);

  return (
    <div className={className}>
      <SectionPanel
        title={title}
        actions={<Verdict result={result} stale={stale} blocked={blocked} />}
      >
        <div className="bg-surface-sunken px-input py-3">
          {editing ? (
            <SampleEditor value={sample} onChange={onSampleChange} />
          ) : (
            <SampleSummary sample={sample} />
          )}
          <div className="mt-3 flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
              {editing ? 'Done' : 'Paste another'}
            </Button>
          </div>
        </div>

        {!secretElsewhere && (
          <div className="border-b border-border-sunken px-input py-3">
            <FieldLabel htmlFor="verify-secret">{secretLabel}</FieldLabel>
            <Input
              id="verify-secret"
              size="row"
              mono
              type="password"
              autoComplete="off"
              placeholder="whsec_…"
              value={secret}
              onChange={(event) => onSecretChange(event.target.value)}
            />
            {!secret && (
              <p className="mt-label text-meta leading-17 text-muted">
                Leave it empty and the check reports the reading only: the header, the fields, the
                string and the time. Whether the signature matches is unknown without it — which is
                not the same as not matching.
              </p>
            )}
          </div>
        )}

        <div className="px-input pt-4 pb-1">
          {!usable ? (
            <p className="text-meta leading-18 text-muted">
              Paste a request that this sender actually sent. Nothing is checked until there is one.
            </p>
          ) : blocked ? (
            <p className="text-meta leading-18 text-muted">{blocked}</p>
          ) : error ? (
            <ErrorState error={error} />
          ) : result ? (
            <VerifySteps steps={verifySteps(result, spec)} />
          ) : (
            <p className="text-meta leading-18 text-muted">Checking…</p>
          )}
        </div>

        {result && !error && !blocked && (
          <p className="px-input pb-3 text-meta leading-18 text-muted">{result.detail}</p>
        )}

        {footer && <div className="px-input pb-input">{footer}</div>}
      </SectionPanel>

      {note && <Note className="mt-4">{note}</Note>}
    </div>
  );
};
